// i18n/index.js - Internationalization engine
// Provides t() function for translations with {placeholder} interpolation

import { locales } from './locales.js';

const STORAGE_KEY = 'scantodesk_locale';
const DEFAULT_LOCALE = 'en';
const SUPPORTED_LOCALES = ['en', 'ko'];

let currentLocale = DEFAULT_LOCALE;
const changeCallbacks = [];

/**
 * Initialize i18n system
 * Priority: localStorage > browser language > default (en)
 */
export function initI18n() {
  // 1. Check localStorage
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED_LOCALES.includes(stored)) {
    currentLocale = stored;
    return;
  }

  // 2. Check browser language
  const browserLang = navigator.language || navigator.userLanguage || '';
  if (browserLang.startsWith('ko')) {
    currentLocale = 'ko';
  } else {
    currentLocale = DEFAULT_LOCALE; // English for all other languages
  }

  // Save detected locale
  localStorage.setItem(STORAGE_KEY, currentLocale);
}

/**
 * Get translation for key with optional parameter interpolation
 * @param {string} key - Translation key (e.g., 'camera.connected')
 * @param {Object} params - Parameters for interpolation (e.g., {name: 'MacBook'})
 * @returns {string} - Translated string
 *
 * @example
 * t('camera.connected', { name: 'MacBook' }) // "MacBook Connected"
 * t('multipage.count', { count: 3 }) // "3 pages"
 */
export function t(key, params = {}) {
  // Try current locale
  let text = locales[currentLocale]?.[key];

  // Fallback to English
  if (text === undefined && currentLocale !== 'en') {
    text = locales.en?.[key];
  }

  // Fallback to key itself
  if (text === undefined) {
    console.warn(`Missing translation: ${key}`);
    return key;
  }

  // Interpolate parameters: {placeholder} syntax
  if (params && Object.keys(params).length > 0) {
    Object.entries(params).forEach(([param, value]) => {
      text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), String(value));
    });
  }

  return text;
}

/**
 * Set current locale
 * @param {string} locale - 'en' or 'ko'
 */
export function setLocale(locale) {
  if (!SUPPORTED_LOCALES.includes(locale)) {
    console.warn(`Unsupported locale: ${locale}`);
    return;
  }

  if (locale === currentLocale) return;

  currentLocale = locale;
  localStorage.setItem(STORAGE_KEY, locale);

  // Notify listeners
  changeCallbacks.forEach(cb => {
    try {
      cb(locale);
    } catch (e) {
      console.error('Locale change callback error:', e);
    }
  });
}

/**
 * Get current locale
 * @returns {string} - 'en' or 'ko'
 */
export function getLocale() {
  return currentLocale;
}

/**
 * Subscribe to locale changes
 * @param {(locale: string) => void} callback
 * @returns {() => void} - Unsubscribe function
 */
export function onLocaleChange(callback) {
  changeCallbacks.push(callback);

  // Return unsubscribe function
  return () => {
    const index = changeCallbacks.indexOf(callback);
    if (index > -1) {
      changeCallbacks.splice(index, 1);
    }
  };
}

/**
 * Get supported locales
 * @returns {string[]}
 */
export function getSupportedLocales() {
  return [...SUPPORTED_LOCALES];
}

/**
 * Check if locale is supported
 * @param {string} locale
 * @returns {boolean}
 */
export function isLocaleSupported(locale) {
  return SUPPORTED_LOCALES.includes(locale);
}
